import { APIClientServices } from "./APIClientServices";

// --- DTOs ---

export type SeniorityLevel = "junior" | "pleno" | "senior";

export interface PricingModelDTO {
	junior?: number;
	pleno?: number;
	senior?: number;
	[key: string]: number | undefined;
}

export type ToolENVType = "number" | "string" | "boolean" | "json" | `OAuth2:${string}`;

export interface ToolENV {
	envName: string;
	type: ToolENVType;
	label: string;
	description?: string;
	required: boolean;
	/** Apenas para envs do tipo OAuth2. Ex: ["email", "profile"] */
	scopes?: string[];
	/** @deprecated use `scopes` */
	oauthScopes?: string[];
	/** Quando true, renderiza como password (oculta o valor). Útil para API keys/tokens. */
	sensitive?: boolean;
	/** Placeholder mostrado no input (ex.: "sk_test_..." ou "abc:xyz"). */
	placeholder?: string;
}

export interface PublicMcpDTO {
	id: string;
	slug: string;
	name: string;
	description: string | null;
	category: string;
	iconUrl: string | null;
	systemPrompt: string;
	mcpServerConfig: Record<string, any>;
	toolEnvFields: ToolENV[];
	pricingModel: PricingModelDTO;
	deliverableTypes: string[];
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface PublicContractedMcpDTO {
	id: string;
	mcpId: string;
	userId: string;
	config: Record<string, any>;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
	mcp?: PublicMcpDTO;
	/** Quantidade de membros (team_member_mcps) vinculados a este contrato. */
	linkedMembersCount?: number;
}

export interface HireMcpPayload {
	mcp_id: string;
	config?: Record<string, any>;
}

export interface ConfigureHiredMcpPayload {
	config: Record<string, any>;
}

export interface InitHiredMcpOAuth2Payload {
	envName?: string;
	callbackUrl: string;
}

export interface UnlinkHiredMcpOAuth2Payload {
	envName?: string;
}

export interface CreateMcpPayload {
	slug: string;
	name: string;
	description?: string;
	category?: string;
	icon_url?: string;
	mcp_server_config?: Record<string, any>;
	tool_env_fields?: ToolENV[];
	pricing_model?: PricingModelDTO;
	deliverable_types?: string[];
}

export interface UpdateMcpPayload {
	name?: string;
	description?: string;
	category?: string;
	icon_url?: string;
	system_prompt?: string;
	mcp_server_config?: Record<string, any>;
	tool_env_fields?: ToolENV[];
	pricing_model?: PricingModelDTO;
	deliverable_types?: string[];
	is_active?: boolean;
}

// --- Service ---

export class McpLibraryServices {
	private static readonly BASE_ROUTE = "v1/backoffice/mcps";
	private static readonly PUBLIC_ROUTE = "v1/mcps";

	/** Lista todos os MCPs (rota pública autenticada). Retorna apenas ativos. */
	static async listMcps(category?: string): Promise<PublicMcpDTO[]> {
		const params: Record<string, string> = {};
		if (category) params.category = category;
		return APIClientServices.get<PublicMcpDTO[]>(this.PUBLIC_ROUTE, params);
	}

	static async listMcpsHired(): Promise<PublicContractedMcpDTO[]> {
		return APIClientServices.get<PublicContractedMcpDTO[]>(`${this.PUBLIC_ROUTE}/hired`);
	}

	static async hireMcp(data: HireMcpPayload): Promise<PublicContractedMcpDTO> {
		return APIClientServices.post<PublicContractedMcpDTO>(`${this.PUBLIC_ROUTE}/hired`, data);
	}

	static async configureHiredMcp(id: string, data: ConfigureHiredMcpPayload): Promise<PublicContractedMcpDTO> {
		return APIClientServices.put<PublicContractedMcpDTO>(`${this.PUBLIC_ROUTE}/hired/${id}`, data);
	}

	static async initHiredMcpOAuth2(id: string, data: InitHiredMcpOAuth2Payload): Promise<{ redirectUrl: string }> {
		return APIClientServices.get<{ redirectUrl: string }>(`${this.PUBLIC_ROUTE}/hired/${id}/oauth2/init`, {
			envName: data.envName,
			callbackUrl: data.callbackUrl,
		});
	}

	static async unlinkHiredMcpOAuth2(id: string, data: UnlinkHiredMcpOAuth2Payload): Promise<PublicContractedMcpDTO> {
		return APIClientServices.post<PublicContractedMcpDTO>(`${this.PUBLIC_ROUTE}/hired/${id}/oauth2/unlink`, data);
	}

	static async setHiredMcpActive(id: string, isActive: boolean): Promise<PublicContractedMcpDTO> {
		return APIClientServices.post<PublicContractedMcpDTO>(`${this.PUBLIC_ROUTE}/hired/${id}/active`, { isActive });
	}

	static async removeHiredMcp(id: string): Promise<{ removed: boolean; unlinkedMembersCount: number }> {
		return APIClientServices.delete<{ removed: boolean; unlinkedMembersCount: number }>(`${this.PUBLIC_ROUTE}/hired/${id}`);
	}

	/** Busca um MCP por slug (rota pública autenticada). */
	static async getMcpBySlug(slug: string): Promise<PublicMcpDTO> {
		return APIClientServices.get<PublicMcpDTO>(`${this.PUBLIC_ROUTE}/${slug}`);
	}

	/** Cria um novo MCP (backoffice, requer MANAGER). */
	static async createMcp(data: CreateMcpPayload): Promise<PublicMcpDTO> {
		return APIClientServices.post<PublicMcpDTO>(this.BASE_ROUTE, data);
	}

	/** Atualiza um MCP existente (backoffice, requer MANAGER). */
	static async updateMcp(id: string | number, data: UpdateMcpPayload): Promise<PublicMcpDTO> {
		return APIClientServices.put<PublicMcpDTO>(`${this.BASE_ROUTE}/${id}`, data);
	}

	/** Desativa (soft delete) um MCP (backoffice, requer MANAGER). */
	static async deleteMcp(id: string | number): Promise<{ message: string }> {
		return APIClientServices.delete<{ message: string }>(`${this.BASE_ROUTE}/${id}`);
	}
}
